import React from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import { Line, LineChart, ResponsiveContainer, Tooltip, TooltipContentProps, XAxis, YAxis } from 'recharts';

export type SparklineChartProps = {
  values: number[];
  /**
   * X-axis labels for each value (e.g. dates). If omitted, falls back to 1..N.
   */
  labels?: string[];
  height?: number;
  /**
   * Max pixel width of the chart container. The chart itself remains responsive.
   */
  maxWidth?: number;
  /**
   * Show hover tooltip. Defaults to true.
   */
  tooltip?: boolean;
};

type Datum = { x: string; v: number };

function toData(values: number[], labels?: string[]): Datum[] {
  return values.map((v, i) => ({ x: labels?.[i] ?? String(i + 1), v }));
}

function SparklineTooltipContent({ active, payload, label }: TooltipContentProps) {
  const theme = useTheme();

  if (!active || !payload?.length) return null;

  const raw = payload[0]?.value;
  const value =
    typeof raw === 'number' ? raw : raw === null || raw === undefined ? '' : Number.isFinite(Number(raw)) ? Number(raw) : String(raw);

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        px: 1,
        py: 0.75,
        boxShadow: theme.shadows[2],
      }}
    >
      <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
          {value}
        </Typography>
      </Stack>
    </Box>
  );
}

export function SparklineChart({ values, labels, height = 32, maxWidth = 120, tooltip = true }: SparklineChartProps) {
  const theme = useTheme();

  if (!values || values.length < 2) {
    return <Box sx={{ height, width: '100%', maxWidth }} />;
  }

  const data = toData(values, labels);

  return (
    <Box sx={{ height, width: '100%', maxWidth }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <XAxis dataKey="x" hide />
          <YAxis hide domain={['dataMin', 'dataMax']} />
          {tooltip ? (
            <Tooltip
              isAnimationActive={false}
              cursor={{ stroke: theme.palette.divider }}
              content={(props: TooltipContentProps) => <SparklineTooltipContent {...props} />}
            />
          ) : null}
          <Line
            type="monotone"
            dataKey="v"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 3 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}

